import { useCallback, useEffect, useMemo, useState } from 'react'
import { IconChevronDown, IconPlus, IconShoppingBag } from '@tabler/icons-react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { LineItem, ListsSortOptions } from 'shared-types'
import { ListSortOptions, ListSortOptionsLabels } from 'shared-types/src/list'
import { Button } from '~/components'
import Card from '~/components/Card/Card'
import { CartItemCard } from '~/components/CartItemCard'
import { CreateCustomerListDialogue } from '~/components/CreateCustomerListDialogue'
import { DropdownMenu } from '~/components/Dropdown'
import { Image } from '~/components/Image'
import AddToCartButton from '~/components/ProductCard/AddToCartButton'
import { ProductPrice } from '~/components/ProductCard/ProductPrice/ProductPrice'
import { ProductSearchModal } from '~/components/ProductSearchModal'
import { SectionContainer } from '~/components/SectionContainer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  useFormatPrice,
  CustomerListActionType,
  CustomerListActions,
  usePage,
  useCustomerListDetail,
  useCartDrawer,
} from '~/hooks'
import { GTM, GTMEvent } from '~/lib'
import styles from './ListDetails.module.css'
import { ListDetailsProps, ListProductItem } from './ListDetails.types'

const ListDetailsActions = [
  { label: 'Rename', value: CustomerListActions.RENAME },
  { label: 'Duplicate list', value: CustomerListActions.DUPLICATE_LIST },
  { label: 'Delete', value: CustomerListActions.DELETE_LIST },
]

export const convertToCartItem = (product: ListProductItem): LineItem => {
  const lineItem: LineItem = {
    id: product?.id,
    price: {
      value: {
        centAmount: 0,
        currencyCode: 'AUD',
        fractionDigits: 0,
        type: 'tt',
      },
    },
    productId: product?.id,
    quantity: product.quantity || 0,
    name: product.title,
    variant: {
      id: 0,
      images: [
        {
          dimensions: {
            height: product?.thumbnail?.width,
            width: product?.thumbnail?.height,
          },
          url: product?.thumbnail?.src,
          label: product?.thumbnail?.altText,
        },
      ],
      assets: [],
      sku: product.sku,
      attributesRaw: [],
    },
    productSlug: product?.url,
  }
  return lineItem
}

export const ListDetails = (props: ListDetailsProps) => {
  const router = useRouter()
  const [isOpenAddProductModal, setIsOpenAddProductModal] = useState(false)
  const { openCartDrawer } = useCartDrawer()
  const [selectedCategories, setSelectedCategories] = useState<
    string[] | undefined
  >()
  const { formatPrice } = useFormatPrice()
  const {
    detail,
    isOpenListNameModal,
    toggleListNameModal,
    renameCustomerList,
    handleCustomerListAction,
    addCustomerListToCart,
    addToCustomerList,
    updateCustomerList,
    removeFromCustomerList,
  } = useCustomerListDetail(props)

  const {
    name,
    productCount,
    sortOptions,
    zeroProductsTitle,
    zeroProductDescription,
    zeroProductsImage,
    products,
    listSummaryTitle,
    listSubtotalTitle,
    listSubtotal,
    listSummaryProductCount,
    browseAllAction,
  } = detail || {}

  const [sortedProducts, setSortedProducts] = useState<ListProductItem[]>([])
  const [selectedSortOption, setSelectedSortOption] = useState<ListSortOptions>(
    ListSortOptions.NAME_ASC
  )
  const [listSortOptions, setListSortOptions] = useState<ListsSortOptions[]>(
    sortOptions || []
  )

  const listId = useMemo(() => {
    return router.asPath.split(/\?|\#/)[0].substring('/lists/'.length)
  }, [router.asPath])

  const sortItems = (itemsToSort: ListProductItem[], sortValue: string) => {
    const sorted = [...itemsToSort]
    switch (sortValue) {
      case ListSortOptions.NAME_ASC:
        return sorted.sort((a, b) => {
          return a.title.localeCompare(b.title)
        })
      case ListSortOptions.NAME_DESC:
        return sorted.sort((a, b) => {
          return b.title.localeCompare(a.title)
        })
      case ListSortOptions.PRICE_ASC:
        return sorted.sort((a, b) => {
          return a.finalPrice.centAmount - b.finalPrice.centAmount
        })
      case ListSortOptions.PRICE_DESC:
        return sorted.sort((a, b) => {
          return b.finalPrice.centAmount - a.finalPrice.centAmount
        })
      default:
        return itemsToSort
    }
  }

  const handleAction = useCallback(
    async (actionType: CustomerListActionType) => {
      await handleCustomerListAction(listId, actionType)

      if (actionType === CustomerListActions.DELETE_LIST) {
        router.push('/lists')
      }
    },
    [listId, router, handleCustomerListAction]
  )

  const handleSort = (value: ListSortOptions) => {
    const selectedOption = listSortOptions.find((option) => {
      return option.value === value
    })
    if (selectedOption) {
      setSelectedSortOption(value)
      setSortedProducts(sortItems(products, value))
    }
  }

  const handleAddListToCart = useCallback(async () => {
    const isSuccess = await addCustomerListToCart(listId)
    if (isSuccess) {
      openCartDrawer()
    }
  }, [listId, addCustomerListToCart, openCartDrawer])

  const page = usePage()
  const isRTM = page.brand === 'rtm'
  const btnStyles = {
    '--btn-icon-size': isRTM ? '20px' : '18px',
  }

  useEffect(() => {
    if (products?.length > 0) {
      setSortedProducts(sortItems(products, selectedSortOption))
    }
  }, [products, selectedSortOption])

  useEffect(() => {
    if (sortOptions?.length > 0) {
      setListSortOptions(sortOptions || [])
    }
  }, [sortOptions])

  return (
    <SectionContainer
      width={props?.width || 'medium'}
      wrapperClassName='!mt-10 lg:!mt-6 3xl:mb-44 2xl:mb-24 lg:mb-16 mb-6'
      innerClassName={classNames(styles.root, 'lg:flex')}>
      <div className={styles.content}>
        <div className='flex justify-between'>
          <div>
            <Typography
              tag={TypographyTag.p}
              variant={
                isRTM ? TypographyVariant.Heading4 : TypographyVariant.Heading5
              }
              className={classNames('!text-black', styles.name)}>
              {name || ''}
            </Typography>
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodyLargeBold}
              className='mt-3 2xl:max-3xl:mt-2 text-black'>
              {productCount || '0'} items
            </Typography>
          </div>
          <div>
            <DropdownMenu
              offset={[Number('-32'), Number('10')]}
              options={ListDetailsActions}
              onChange={handleAction}>
              <div className='flex items-center gap-2 border border-primary-heading rounded py-2 px-3'>
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodyRegularBold}
                  className='text-primary-heading'>
                  Edit
                </Typography>
                <IconChevronDown
                  size={18}
                  color='var(--color-typography-body)'
                />
              </div>
            </DropdownMenu>
          </div>
        </div>
        {!!listSortOptions?.length && (
          <div className='pt-6'>
            <DropdownMenu
              offset={[0, Number('10')]}
              options={listSortOptions}
              onChange={handleSort}>
              <div className='flex items-center gap-2 py-2.5 px-4 bg-greyscale-100 rounded-3xl'>
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodySmall}
                  className='text-primary-heading'>
                  <span>Sort By</span>{' '}
                  <span className={isRTM ? 'font-bold' : 'font-normal'}>
                    {ListSortOptionsLabels[selectedSortOption]}
                  </span>
                </Typography>
                <IconChevronDown size={20} />
              </div>
            </DropdownMenu>
          </div>
        )}
        <div className='mt-8'>
          {sortedProducts?.length > 0 ? (
            <>
              <div className='flex flex-col gap-6 pb-6'>
                {sortedProducts?.map((product, index) => {
                  const item = convertToCartItem(product)
                  return (
                    <div
                      key={index}
                      className='pb-6 border-b flex gap-1 sm:gap-4 w-full justify-between'>
                      <CartItemCard
                        item={item}
                        isEditable
                        isList
                        isLoading={false}
                        canRemove
                        onChangeQuantity={(qty) => {
                          updateCustomerList(listId, product.sku, qty)
                        }}
                        removeCartItem={() => {
                          removeFromCustomerList(listId, [product.sku])
                        }}
                      />
                      <div className='min-w-[40px]'>
                        <div className='flex flex-col justify-between gap-2 items-end min-h-full [&>div]:!mt-0'>
                          <ProductPrice
                            isFeatured={false}
                            regularPrice={product.regularPrice}
                            finalPrice={product.finalPrice}
                            finalPriceClassName='text-sm leading-4'
                          />
                          <AddToCartButton
                            sku={product.sku}
                            inStock={product.inStock}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              <Button
                status='secondary'
                icon={<IconPlus />}
                style={btnStyles}
                onClick={() => {
                  return setIsOpenAddProductModal(true)
                }}>
                Add products
              </Button>
            </>
          ) : (
            <div className='flex flex-col items-center gap-8 text-center'>
              <div className='relative'>
                <Image
                  src={zeroProductsImage}
                  width='195'
                  height='196'
                  alt='empty list'
                />
              </div>
              <div>
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodyLargeBold}
                  className='text-black'>
                  {zeroProductsTitle}
                </Typography>
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodyRegularExtraLineHeight}
                  className='mt-3 whitespace-pre-wrap text-secondary-muted'>
                  {zeroProductDescription}
                </Typography>
              </div>
              <div className='flex flex-col items-center gap-4'>
                <Button
                  status='secondary'
                  onClick={() => {
                    return setIsOpenAddProductModal(true)
                  }}
                  style={{
                    ...btnStyles,
                    boxShadow: '0px 1px 2px -1px rgba(17, 12, 34, 0.08)',
                  }}
                  icon={<IconPlus size={20} />}>
                  <Typography
                    tag={TypographyTag.p}
                    variant={TypographyVariant.BodyRegularBold}
                    className='text-primary-blue ml-2 tracking-button-letter-spacing'>
                    Add products
                  </Typography>
                </Button>
                {!!browseAllAction?.href && (
                  <Button
                    variant='ghost'
                    href={browseAllAction?.href}
                    className='py-2 px-3'
                    style={{
                      '--btn-solid-color-bg-secondary-hover': 'transparent',
                      '--btn-border-width': '0',
                      '--btn-ghost-color-text-primary-hover':
                        'var(--color-typography-heading)',
                    }}
                    noHover>
                    {browseAllAction?.title || 'Browse All'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={classNames(styles.side, 'mt-8 lg:mt-0')}>
        <Card
          variant='light'
          title={
            <Typography
              tag={TypographyTag.span}
              variant={TypographyVariant.BodyLargeBold}>
              {listSummaryTitle}
            </Typography>
          }>
          <div className='flex items-start justify-between py-2 border-b border-primary-border'>
            <div className='flex flex-col gap-1'>
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.BodyRegular}
                className='text-secondary-muted'>
                {listSubtotalTitle}
              </Typography>
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.BodyRegular}
                className='text-secondary-muted'>
                ({listSummaryProductCount || '0'} products)
              </Typography>
            </div>
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodyRegular}
              className='text-secondary-muted'>
              {formatPrice(listSubtotal)}
            </Typography>
          </div>
          <Button
            theme={{ root: 'mt-6' }}
            style={btnStyles}
            fluid
            onClick={handleAddListToCart}
            icon={<IconShoppingBag />}
            disabled={productCount === 0}>
            Add list items to cart
          </Button>
        </Card>
      </div>
      <ProductSearchModal
        open={isOpenAddProductModal}
        onClose={() => {
          return setIsOpenAddProductModal(false)
        }}
        onSelect={(product) => {
          GTM.dispatch(GTMEvent.ADD_TO_LIST, { product })
          return addToCustomerList(listId, [product.sku])
        }}
      />

      <CreateCustomerListDialogue
        open={isOpenListNameModal}
        title='Rename list'
        label='What would you like to name your list?'
        confirmButtonText='Rename List'
        defaultValue={name || ''}
        confirmAction={(v: string) => {
          return renameCustomerList(listId, v)
        }}
        onClose={() => {
          return toggleListNameModal(false)
        }}
      />
    </SectionContainer>
  )
}
